import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Clock } from "lucide-react";

const TIERS = [
  {
    key: "reader", name: "READER", price: "Free", tag: "Free account",
    features: ["Breaking news & market updates", "Beginner education", "Selected articles & briefs", "Watchlists & saved research", "Community access"],
  },
  {
    key: "alpha", name: "ALPHA", price: "White List", tag: "Premium", featured: true,
    features: ["Everything in Reader", "Full research & alpha feed", "Smart-money intelligence", "Conviction tracker & performance", "On-chain alerts", "The Lyfe Brief (daily)", "Premium community", "Selected events"],
  },
  {
    key: "private", name: "PRIVATE", price: "By application", tag: "Invitation",
    features: ["Everything in Alpha", "Private research calls", "Investor gatherings", "Founder access", "Private events", "Curated networking"],
  },
];

export default function Pricing() {
  const { user, isAlpha, requestAlpha } = useAuth();
  const navigate = useNavigate();
  const status = user?.alpha_request?.status || "none";
  const waitlisted = status === "waitlisted";

  const join = async (key) => {
    if (!user) { navigate("/register"); return; }
    if (isAlpha) { toast.success("You already have active access."); return; }
    try {
      await requestAlpha(key, "");
      toast.success("You've joined the waitlist. An admin will review and approve your access.");
    } catch {
      toast.error("Could not submit request.");
    }
  };

  const ctaFor = (key) => {
    if (key === "reader") return { label: user ? "Your Free Tier" : "Create Account", disabled: !!user };
    if (isAlpha) return { label: "Access Active", disabled: true };
    if (waitlisted) return { label: "Joined Waitlist", disabled: true, wait: true };
    return { label: key === "private" ? "Request Access" : "Join Waitlist", disabled: false };
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-16">
      <div className="text-center max-w-2xl mx-auto mb-6">
        <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-3">Membership</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">A premium intelligence subscription.</h1>
        <p className="text-zinc-400 mt-4">
          Subscribe to Alpha, then join the waitlist. Access is confirmed once an admin approves your membership.
        </p>
        {user && (
          <p className="mt-4 font-mono-cl text-xs tracking-[0.14em] uppercase text-zinc-400">
            Current tier: <span className="text-[#ccff00]">{user.role === "admin" ? "Admin (all access)" : user.tier}</span>
          </p>
        )}
      </div>

      {waitlisted && !isAlpha && (
        <div data-testid="waitlist-banner" className="max-w-xl mx-auto mb-10 rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/[0.05] p-5 flex items-center gap-3">
          <Clock className="w-5 h-5 text-[#ccff00] shrink-0" />
          <p className="text-sm text-zinc-200">You're on the waitlist. We'll unlock Alpha as soon as an admin approves your access.</p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 items-start">
        {TIERS.map((t) => {
          const cta = ctaFor(t.key);
          return (
            <div key={t.key} data-testid={`pricing-${t.key}`}
              className={`relative rounded-3xl border p-8 ${t.featured ? "border-[#ccff00]/50 bg-[#ccff00]/[0.03] glow-brand md:-translate-y-3" : "border-white/10 bg-[#0a0a0a]"}`}>
              {t.featured && <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#ccff00] text-black text-[10px] font-bold tracking-[0.14em] uppercase">Most Popular</span>}
              <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{t.tag}</p>
              <h3 className="font-display text-3xl font-black">{t.name}</h3>
              <div className="flex items-end gap-1 mt-3 mb-6">
                <span className="font-display text-4xl font-black">{t.price}</span>
                {t.per && <span className="text-zinc-500 mb-1.5 text-sm">{t.per}</span>}
              </div>
              <button data-testid={`pricing-cta-${t.key}`}
                onClick={() => (t.key === "reader" && !user ? navigate("/register") : join(t.key))}
                disabled={cta.disabled}
                className={`w-full py-3 rounded-full font-semibold text-sm transition-[filter,background-color] flex items-center justify-center gap-2 ${
                  cta.disabled ? "border border-white/10 text-zinc-500 cursor-default" : t.featured ? "bg-[#ccff00] text-black hover:brightness-110" : "border border-white/15 text-white hover:border-[#ccff00]/50"
                }`}>
                {cta.wait && <Check className="w-4 h-4" />}
                {cta.label}
              </button>
              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-3 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-[#ccff00] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
